<script setup lang="ts">
import {computed, ref, onMounted, watch} from 'vue';
import { useActivationStore, useLocalizationStore } from 'src/store';
import { type CanaryPatientCaregiver } from 'src/types/webContracts';
import { getCanaryCaregiverDetail, updateCanaryCaregiverDetail } from 'src/api/endpoints/patientWeb';
import { getNextActivationRoute } from 'src/services/piqSetupService';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { custom_moment } from '@/plugins/moment-cultured';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import {useI18n} from 'vue-i18n';

// components
import MultiViewLayout from 'src/components/layouts/MultiViewLayout.vue';

import TheAbandonModal from '@/components/TheAbandonModal.vue';
import TheContactModal from '@/components/TheContactModal.vue';

const localStore = useLocalizationStore();
const activationStore = useActivationStore();
const $q = useQuasar();
const { t } = useI18n();

const defaulCountryCode = 'US';
const defaulCountryCallingCode = '1';
const patientCaregiver = ref<CanaryPatientCaregiver>(getDefaultCareGiver());
const formIsValid = ref(true);
const maxDate: Date = custom_moment().add(-18, 'year').toDate();

const careGiverForm = ref();
const birthdate = ref<Date | null>(null);
const countryList = computed(() => activationStore.supportedCountries);
const phoneNumber = ref({
  value: '',
  phoneObject: {}
});

const altPhoneNumber = ref({
  value: '',
  phoneObject: {}
});

const router = useRouter();

onMounted(async () => {
  $q.loading.show()
  await getCanaryCaregiverDetail()
    .then(async (response) => {
      const careGiver = await toCanaryPatientCaregiver(response?.data?.result);
      patientCaregiver.value = (careGiver && careGiver?.email && careGiver?.lastName) ? careGiver : getDefaultCareGiver();
    })
    .finally(() => {
      $q.loading.hide()
    });
});

// methods
async function onNextClick() {

  let isFormValid = await careGiverForm.value.validate();
  if (!isFormValid) return;


  // values from phone and date pickers
  patientCaregiver.value.phone = phoneNumber.value?.value;
  patientCaregiver.value.phoneCountry = phoneNumber.value?.phoneObject?.countryCallingCode;

  if (altPhoneNumber.value?.phoneObject?.valid) {
    patientCaregiver.value.alternatePhone = altPhoneNumber.value?.value;
    patientCaregiver.value.alternatePhoneCountry = altPhoneNumber.value?.phoneObject?.countryCallingCode;
  } else {
    patientCaregiver.value.alternatePhone = '';
    patientCaregiver.value.alternatePhoneCountry = '';
  }

  patientCaregiver.value.birthdate = custom_moment(birthdate.value).format('YYYY-MM-DD');

  $q.loading.show()
  const response = await handleApiRequest(() =>
    updateCanaryCaregiverDetail(patientCaregiver.value), null, $q
  ).finally(() => {
    $q.loading.hide()
  });

  if (!response?.result) {
    //error return
  } else {
    var nextRoute = await getNextActivationRoute($q);
    router.push({ name: nextRoute }).catch(() => { });
  }
};

async function toCanaryPatientCaregiver(response: CanaryPatientCaregiver) {
  if (!response) return null;

  const phoneCountryCallingCode = Number(response.phoneCountry) > 0
    ? response.phoneCountry
    : defaulCountryCallingCode;
  const countryCodePhoneNumber = `+${phoneCountryCallingCode}${response.phone ?? ''}`;
  phoneNumber.value.value = countryCodePhoneNumber;

  const alternatePhoneCountryCallingCode = Number(response.alternatePhoneCountry) > 0
    ? response.alternatePhoneCountry
    : defaulCountryCallingCode;
  const altCountryCodePhoneNumber = `+${alternatePhoneCountryCallingCode}${response.alternatePhone ?? ''}`;
  altPhoneNumber.value.value = altCountryCodePhoneNumber;

  const responseBirthDate = new Date(response?.birthdate);
  if (responseBirthDate.getFullYear() > 1900) {
    birthdate.value = responseBirthDate;
  }

  return {
    firstName: response?.firstName,
    middleName: response?.middleName,
    lastName: response?.lastName,
    email: response?.email,
    phoneCountry: phoneCountryCallingCode,
    phone: phoneNumber.value.value,
    alternatePhoneCountry: alternatePhoneCountryCallingCode,
    alternatePhone: altCountryCodePhoneNumber,
    city: response?.city,
    country: response?.country,
    birthdate: response?.birthdate,
    relationshipToPatient: response?.relationshipToPatient,
    address: response?.address,
    zipcode: response?.zipcode,
    state: response?.state,
  } as CanaryPatientCaregiver;
}

function getDefaultCareGiver(): CanaryPatientCaregiver {
  return {
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    country: localStore.region,
    phone: '',
    phoneCountry: defaulCountryCallingCode,
    relationshipToPatient: '',
    alternatePhone: '',
    alternatePhoneCountry: defaulCountryCallingCode,
    birthdate: '',
    address: '',
    zipcode: '',
    state: '',
    city: ''
  };

}

const showContact = ref(false);

async function onInfoClick() {
  showContact.value = !showContact.value;
}

const showAbandon = ref(false);

async function onAbandonClick() {
  showAbandon.value = !showAbandon.value;
}
</script>

<template>
  <q-page padding class="canary setup caregiver">
    <q-form ref="careGiverForm" greedy>
      <MultiViewLayout :onBackClick="onAbandonClick" back-icon-list="fa-solid fa-house-circle-xmark"
                       :box-padding="$zb.enums.BoxPaddingEnum.p303048">
        <template v-slot:title>
          {{ t('hbs_flow_title') }}
        </template>

        <!-- Title -->
        <template #heading>
          {{ t('piq_caregiver_details') }}
        </template>

        <template v-slot:auxiliary>
          <zbm-btn-icon @click="onInfoClick()" icon="fa-regular fa-question-circle" size="24px" text-color="dark-teal"/>
        </template>

        <!-- View Unique Content -->
        <template #default>

          <!-- Name -->
          <div class="row form-row">

            <!-- First -->
            <div class="col">
              <zbm-input-with-validation v-model="patientCaregiver.firstName"
                                         autofocus
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         :label="t('FirstName')"
                                         :rules="[$zb.validators.required('survey_answer_required'), $zb.validators.maxLen(64)]" />
            </div>

            <!-- Middle -->
            <div class="col">
              <zbm-input-with-validation v-model="patientCaregiver.middleName"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         :label="t('piq_middlename')"
                                         :rules="[$zb.validators.maxLen(64)]" />
            </div>

            <!-- Last -->
            <div class="col">
              <zbm-input-with-validation v-model="patientCaregiver.lastName"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         :label="t('LastName')"
                                         :rules="[$zb.validators.required('survey_answer_required'), $zb.validators.maxLen(64)]" />
            </div>
          </div>

          <!-- Phones -->
          <div class="row form-row">

            <!-- Mobile -->
            <div class="col-auto">
              <zbm-telephone-with-validation v-model="phoneNumber"
                                             :label="t('activate_phone_number')"
                                             :patient-country-code="defaulCountryCode"
                                             :rules="[$zb.validators.phoneRequired('survey_answer_required')]" />
            </div>

            <!-- Additional -->
            <div class="col-auto">
              <zbm-telephone-with-validation v-model="altPhoneNumber"
                                             :label="t('piq_additionalphone')"
                                             :patient-country-code="defaulCountryCode" />
            </div>
          </div>

          <!-- Email -->
          <div class="row form-row">
            <div class="col-auto">
              <zbm-input-with-validation v-model="patientCaregiver.email"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormEl300"
                                         :label="t('email_address')"
                                         :rules="[$zb.validators.required('activate_profile_email_required'), $zb.validators.email()]"
                                         type="email" />
            </div>
          </div>

          <!-- DOB, Relationship -->
          <div class="row form-row">

            <!-- Date of Birth -->
            <div class="col-auto">
              <zbm-date-picker v-model="birthdate"
                               :label="t('activate_date_of_birth')"
                               :maxDate="maxDate"
                               placeholder=" "
                               :rules="[$zb.validators.required()]" />
            </div>

            <!-- Relationship to Patient -->
            <div class="col">
              <zbm-input-with-validation v-model="patientCaregiver.relationshipToPatient"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         :label="t('piq_relationtopatient')"
                                         :rules="[$zb.validators.maxLen(128), $zb.validators.required('survey_answer_required')]" />
            </div>
          </div>

          <!-- City, State -->
          <div class="row form-row">

            <!-- City -->
            <div class="col">
              <zbm-input-with-validation v-model="patientCaregiver.city"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         :label="t('piq_address_city')"
                                         :rules="[$zb.validators.required('survey_answer_required'), $zb.validators.maxLen(25)]" />
            </div>

            <!-- State -->
            <div class="col-auto">
              <zbm-select-with-validation v-model="patientCaregiver.country"
                                          :label="t('piq_country')"
                                          option-label="text"
                                          option-value="value"
                                          :options="countryList.countryOptions"
                                          :rules="[$zb.validators.required()]" />
            </div>

          </div>
          <div class="row pt-48">
            <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" :label="t('acc_gen_continue')" :outline="true"
                     :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                     :disabled="!formIsValid" @click="onNextClick"/>
          </div>
        </template>

<!--        <template v-slot:footer>-->
<!--          <Box class="col-auto" :boxWidth="$zb.enums.BoxWidthEnum.lg" :boxPadding="$zb.enums.BoxPaddingEnum.p20"-->
<!--               :boxDropShadow="$zb.enums.BoxDropShadowEnum.lg">-->
<!--            <ProgressBar :backEnabled="true" :progress="80" v-model:nextEnabled="formIsValid"-->
<!--                         @on-back-click="onBackClick" @on-next-click="onNextClick" />-->
<!--          </Box>-->
<!--          -->
<!--        </template>-->

      </MultiViewLayout>
    </q-form>
    <the-abandon-modal v-model="showAbandon"/>

    <the-contact-modal v-model="showContact"/>
  </q-page>
</template>

<style lang="scss" scoped></style>
